/**
 * OpenList 树节点组件
 */

"use client"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  Folder,
  FolderOpen,
  Loader2,
} from "lucide-react"
import type { OpenListTreeNode } from "@/types/openlist"
import { formatFileSize } from "@/lib/utils/openlist-file-utils"
import { OpenListFileIcon } from "./openlist-file-icon"

interface OpenListTreeNodeProps {
  node: OpenListTreeNode
  expandedFolders: Set<string>
  loadingNodes: Set<string>
  clickedFileId: string | null
  isDownloading: Set<string>
  onToggleFolder: (node: OpenListTreeNode) => void
  onPreview: (file: any) => void
  onDownload: (file: any) => void
}

export function OpenListTreeNode({
  node,
  expandedFolders,
  loadingNodes,
  clickedFileId,
  isDownloading,
  onToggleFolder,
  onPreview,
  onDownload
}: OpenListTreeNodeProps) {
  const isExpanded = expandedFolders.has(node.path)
  const isLoading = loadingNodes.has(node.path)
  const hasChildren = node.children.length > 0 || (node.type === 'folder' && !node.isLoaded)

  if (node.type === 'folder') {
    return (
      <div className="mb-2">
        {/* 文件夹节点 */}
        <div
          className="flex items-center p-2 hover:bg-gray-50 rounded-lg cursor-pointer group"
          style={{ marginLeft: `${node.level * 1}rem` }}
          onClick={() => onToggleFolder(node)}
        >
          <div className="flex items-center flex-1">
            {hasChildren && (
              <>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-gray-400 mr-2" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-gray-400 mr-2" />
                )}
              </>
            )}
            {!hasChildren && <div className="w-4 mr-2" />}
            {isExpanded ? (
              <FolderOpen className="h-4 w-4 text-blue-500" />
            ) : (
              <Folder className="h-4 w-4 text-yellow-500" />
            )}
            <span className="ml-2 font-medium text-gray-700 group-hover:text-gray-900">
              {node.name}
            </span>
            {isLoading && (
              <Loader2 className="h-4 w-4 ml-2 animate-spin text-gray-400" />
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant="secondary" className="text-xs">
              文件夹
            </Badge>
            {node.folderData && (
              <span className="text-xs text-gray-500">
                {formatFileSize(node.folderData.size)}
              </span>
            )}
          </div>
        </div>

        {/* 展开的子节点 */}
        {isExpanded && node.children.length > 0 && (
          <div className="mt-2">
            {node.children.map(childNode => (
              <OpenListTreeNode
                key={childNode.id}
                node={childNode}
                expandedFolders={expandedFolders}
                loadingNodes={loadingNodes}
                clickedFileId={clickedFileId}
                isDownloading={isDownloading}
                onToggleFolder={onToggleFolder}
                onPreview={onPreview}
                onDownload={onDownload}
              />
            ))}
          </div>
        )}
      </div>
    )
  } else {
    // 文件节点
    const isDownloadingFile = isDownloading.has(node.path)
    const isClicked = clickedFileId === node.path
    
    return (
      <div className="mb-1">
        <div
          className={`flex items-center p-2 rounded-lg group transition-colors ${
            isClicked
              ? "bg-blue-50 border border-blue-200"
              : "hover:bg-gray-50"
          }`}
          style={{ marginLeft: `${node.level * 1 + 0.5}rem` }}
        >
          <div className="flex items-center flex-1">
            <OpenListFileIcon fileType={node.fileData?.fileType || 'unknown'} />
            <span className="ml-2 text-gray-700 group-hover:text-gray-900">
              {node.name}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {node.fileData && (
              <span className="text-xs text-gray-500">
                {formatFileSize(node.fileData.size)}
              </span>
            )}
            <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => node.fileData && onPreview(node.fileData)}
                disabled={isClicked}
              >
                {isClicked ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Eye className="h-3 w-3" />
                )}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-6 w-6 p-0"
                onClick={() => node.fileData && onDownload(node.fileData)}
                disabled={isDownloadingFile}
              >
                {isDownloadingFile ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Download className="h-3 w-3" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }
}
